import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { map } from 'rxjs';
import { FeatureFlagsService } from './feature-flags.service';

/**
 * Blocks a route whose feature flag is off, so a hidden section cannot be
 * reached by typing its URL. Hiding the navigation entry alone is not a gate.
 *
 * Usage — `data: { feature: 'surveys' }` on the route.
 *
 * Sends the user to their org dashboard rather than a 404: the URL is valid,
 * the feature simply isn't switched on for this deployment.
 */
export const featureEnabledGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const featureKey = route.data?.['feature'] as string | undefined;
    const router = inject(Router);

    if (!featureKey) {
        return true;
    }

    return inject(FeatureFlagsService)
        .load()
        .pipe(
            map((enabled) => {
                if (enabled.has(featureKey)) {
                    return true;
                }
                // First path segment is the org slug (routes are /:slug/...).
                const slug = state.url.split('/').filter(Boolean)[0];
                return router.createUrlTree(
                    slug ? ['/', slug, 'dashboard'] : ['/']
                );
            })
        );
};
